/**
 * Sample graph templates — pre-wired graphs users can load with one click.
 * All targets default to the context SceneObject (the object the BehaviorGraph
 * is attached to). Users can change targets afterwards in the node UI.
 *
 * A sample is a STARTING POINT: it stamps ordinary nodes the author then edits,
 * and keeps no identity of its own. That is what separates it from a macro,
 * which stays one logical block (trace-viewer macros/). Both appear in this
 * panel, so they are named differently on purpose:
 *
 *   - a **block** is named for the operation it performs — "Toggle Active"
 *   - a **sample** is named for the scenario it sets up — "Click to Toggle"
 *
 * Never give a sample the same name as a block: a sample that wraps one macro
 * in a trigger is a different thing from the macro, and two identically-named
 * entries in one panel is the confusion this naming avoids.
 */

import { GraphBuilder } from 'trace-viewer/behaviors';
import type { GraphData } from 'trace-viewer/behaviors';
import type { SceneObject } from 'trace-viewer/scene';

export interface SampleGraph {
    name: string;
    description: string;
    build: (context: SceneObject) => GraphData;
}

export const SAMPLE_GRAPHS: SampleGraph[] = [
    // ──────────────────────────────────────────────────
    // Click to Toggle — trigger + the Toggle Active block
    //
    // Named for the scenario, not the operation: the block it places is called
    // "Toggle Active", and two entries with the same name in one panel is
    // exactly the confusion this naming avoids.
    // ──────────────────────────────────────────────────
    {
        name: 'Click to Toggle',
        description: 'Click this object to switch it on/off',
        build: (ctx) => new GraphBuilder(ctx)
            .onSelect()
                .toggleActive(ctx)
            .build(),
    },

    // ──────────────────────────────────────────────────
    // Show on Start
    // ──────────────────────────────────────────────────
    {
        name: 'Show on Start',
        description: 'Make this object visible when the scene starts',
        build: (ctx) => new GraphBuilder(ctx)
            .onStart()
                .setActive(ctx, true)
            .build(),
    },

    // ──────────────────────────────────────────────────
    // Hide on Start
    // ──────────────────────────────────────────────────
    {
        name: 'Hide on Start',
        description: 'Hide this object when the scene starts',
        build: (ctx) => new GraphBuilder(ctx)
            .onStart()
                .setActive(ctx, false)
            .build(),
    },

    // ──────────────────────────────────────────────────
    // Delayed Reveal
    // ──────────────────────────────────────────────────
    {
        name: 'Delayed Reveal',
        description: 'Hide this object on start, then show it after 2 seconds',
        build: (ctx) => new GraphBuilder(ctx)
            .onStart()
                .setActive(ctx, false)
                .delay(2)
                    .whenDone.setActive(ctx, true)
            .build(),
    },

    // ──────────────────────────────────────────────────
    // Show A or B (Branch)
    // ──────────────────────────────────────────────────
    {
        name: 'Show A or B',
        description: 'Click to show this object if active, or hide if inactive',
        build: (ctx) => {
            const b = new GraphBuilder(ctx);
            const active = b.addGetActiveNode(ctx);
            const br = b.onSelect().branch(active);
            br.onTrue().setActive(ctx, true);
            br.onFalse().setActive(ctx, false);
            return b.build();
        },
    },

    // ──────────────────────────────────────────────────
    // Show Multiple (Sequence)
    // ──────────────────────────────────────────────────
    {
        name: 'Show Multiple',
        description: 'Click to show this object via two sequential steps',
        build: (ctx) => {
            const b = new GraphBuilder(ctx);
            const seq = b.onSelect().sequence(2);
            seq.step(0).setActive(ctx, true);
            seq.step(1).setActive(ctx, true);
            return b.build();
        },
    },

    // ──────────────────────────────────────────────────
    // Activate Random Child
    // ──────────────────────────────────────────────────
    {
        name: 'Random Child',
        description: 'Click to activate a random child (hides siblings)',
        build: (ctx) => {
            const b = new GraphBuilder(ctx);
            const count = b.addGetChildCountNode(ctx);
            const rand = b.addRandomNode(0, { node: count, socket: 'value' });
            const floored = b.addFloorNode(rand);
            b.onSelect().setActiveChildFrom(ctx, floored);
            return b.build();
        },
    },

    // ──────────────────────────────────────────────────
    // Toggle Property
    // ──────────────────────────────────────────────────
    {
        name: 'Toggle Property',
        description: 'Click to toggle a boolean property on this object',
        build: (ctx) => {
            const b = new GraphBuilder(ctx);
            const prop = b.addGetPropertyNode(ctx, 'visible');
            const notted = b.addNotNode(prop);
            b.onSelect().setPropertyFrom(ctx, 'visible', notted);
            return b.build();
        },
    },

    // ──────────────────────────────────────────────────
    // Click to Blink — trigger + Interval + Toggle Active
    // ──────────────────────────────────────────────────
    {
        name: 'Click to Blink',
        description: 'Click to start blinking this object once per second (Interval macro)',
        build: (ctx) => {
            // interval() emits the Interval canned composition — standard
            // Sequence + Delay nodes tagged with macro metadata. The editor
            // collapses the recognized expansion into ONE Interval node.
            const b = new GraphBuilder(ctx);
            b.onSelect().interval(1).whenDone.toggleActive(ctx);
            return b.build();
        },
    },

    // ──────────────────────────────────────────────────
    // Play Media on Click
    // ──────────────────────────────────────────────────
    {
        name: 'Click to Play Media',
        description: 'Click this object to play/resume its media (video, audio, animation, pages)',
        build: (ctx) => new GraphBuilder(ctx)
            .onSelect()
                .playMedia(ctx)
            .build(),
    },

    // ──────────────────────────────────────────────────
    // Random Color on Start
    // ──────────────────────────────────────────────────
    {
        name: 'Random Color',
        description: 'Set a color property to a random value on start',
        build: (ctx) => {
            const b = new GraphBuilder(ctx);
            const r = b.addRandomNode(0, 1);
            const g = b.addRandomNode(0, 1);
            const bl = b.addRandomNode(0, 1);
            const color = b.addCombine3Node(r, g, bl);
            b.onStart().setPropertyFrom(ctx, 'color', color);
            return b.build();
        },
    },

    // ──────────────────────────────────────────────────
    // Play Animation on Click
    // ──────────────────────────────────────────────────
    {
        name: 'Click to Play Animation',
        description: 'Click this object to play an animation clip on it',
        build: (ctx) => new GraphBuilder(ctx)
            .onSelect()
                .playAnimation(ctx)
            .build(),
    },

    // ──────────────────────────────────────────────────
    // Animation Sequence
    // ──────────────────────────────────────────────────
    {
        name: 'Animation Sequence',
        description: 'Click to play two animations in sequence',
        build: (ctx) => new GraphBuilder(ctx)
            .onSelect()
                .playAnimation(ctx)
                .thenPlay(ctx)
            .build(),
    },
];
